/**
 * Shared financing math — the SINGLE source of truth for the buyer-facing
 * payment estimate. Lifted out of FinanceInsurance.jsx so the vehicle page's
 * price row and the F&I "Estimate your payment" screen can never disagree.
 *
 * Nothing here is an offer of credit. `fi.financing.disclaimer` is the one
 * disclaimer string both surfaces render.
 */

import i18n from './i18n';

// Phase 1 illustrative rate — the buyer screen never claims a real approval.
export const APR = 0.079;

// ── MONEY AND NUMBERS, IN THE READER'S LANGUAGE ─────────────────────────────
// A Québec dealer reads "48 000 $", not "$48,000": in French the symbol goes
// AFTER the amount and thousands are separated by a space. Hard-coding `$` in
// front of `toLocaleString()` got this wrong on every French screen, so every
// money string in the automotive surfaces now comes through here.
//
// es-MX (not plain es) is deliberate: it keeps "$48,000", which matches the
// Spanish copy already in the product ("${{amount}} DE DESCUENTO"). Plain `es`
// is Spain and would render "48.000 $".
const NUMBER_LOCALES = { en: 'en-CA', fr: 'fr-CA', es: 'es-MX' };

const activeLocale = () => {
    // Read lazily: the reader can change language without a reload.
    const lang = i18n?.language || 'en';
    return NUMBER_LOCALES[String(lang).slice(0, 2)] || 'en-CA';
};

/** Whole-number amount with the currency symbol where the language puts it. */
export const fmtCurrency = (n) => new Intl.NumberFormat(activeLocale(), {
    style: 'currency', currency: 'CAD', currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(Math.round(Number(n) || 0));

/** A bare number (mileage, or an amount whose translation supplies the symbol). */
export const fmtNumber = (n) => new Intl.NumberFormat(activeLocale()).format(Number(n) || 0);

/**
 * `asking_price` is dealer free text ("47995", "$47,995", "Call for price",
 * "$47,995 + taxes"). Reformat ONLY when the whole string is just an amount —
 * anything the dealer wrote around the number is theirs and is left alone.
 * This is why a French buyer was reading "$41,995" on the hero.
 */
export const priceText = (askingPrice) => {
    const s = String(askingPrice ?? '').trim();
    if (!s) return '';
    if (!/^\$?\s*[\d][\d.,\s\u00a0\u202f]*\$?$/.test(s)) return s;
    const n = parsePriceValue(s);
    return n > 0 ? fmtCurrency(n) : s;
};

/**
 * Same story for mileage: "28,400 km" must read "28 400 km" in French. Only
 * the NUMBER is rewritten, so the dealer's own unit/suffix survives.
 */
export const mileageText = (mileage) => {
    const s = String(mileage ?? '');
    const m = s.match(/\d[\d.,\s\u00a0\u202f]*\d|\d/);
    if (!m) return s;
    const n = Number(m[0].replace(/[^0-9.]/g, ''));
    return n > 0 ? s.replace(m[0], fmtNumber(Math.round(n))) : s;
};

export const FREQ = [['biweekly', 26], ['weekly', 52], ['monthly', 12]];
export const TERMS = [36, 48, 60, 72];

/** F&I screen's landing state, so the price row mirrors it exactly. */
export const DEFAULT_TERM_MONTHS = 60;
export const DEFAULT_DOWN_RATE = 0.1;

export const money = (n) => fmtCurrency(n);

/** Simple amortised payment for the chosen frequency. Display only. */
export const payment = (principal, months, freq) => {
    const perYear = (FREQ.find(([k]) => k === freq) || FREQ[0])[1];
    const n = Math.round((months / 12) * perYear);
    const i = APR / perYear;
    if (principal <= 0 || n <= 0) return 0;
    return (principal * i) / (1 - Math.pow(1 + i, -n));
};

/**
 * `asking_price` is free text in Mongo ("47995", "$47,995", "Call for price"),
 * so every caller has to strip it. Returns 0 when there is no number to use.
 */
export const parsePrice = (askingPrice) => parsePriceValue(askingPrice);

// Defined separately so `priceText` above can use it without a forward ref.
function parsePriceValue(askingPrice) {
    return Number(String(askingPrice || '').replace(/[^0-9.]/g, '')) || 0;
}

/** The F&I screen's own default down payment for a given price. */
export const defaultDown = (price) => Math.round(price * DEFAULT_DOWN_RATE);

/**
 * The price row's monthly estimate. Same `payment()`, same APR, and the SAME
 * inputs the F&I screen lands on (60 months, 10% down) — only the frequency is
 * pinned to monthly, because the price row speaks in "/mo". Flip F&I's own
 * frequency to Monthly for this vehicle and you get this identical figure.
 *
 * Returns null when the price does not parse, so the caller can fall back to
 * the price-only layout instead of printing "$0/mo".
 */
export const monthlyEstimate = (askingPrice) => {
    const price = parsePrice(askingPrice);
    if (price <= 0) return null;
    const financed = Math.max(price - defaultDown(price), 0);
    const per = payment(financed, DEFAULT_TERM_MONTHS, 'monthly');
    return per > 0 ? per : null;
};
